import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { errMsg } from "@/lib/api";
import { ambApi, ambToken } from "@/lib/ambApi";

const input = "h-12 w-full rounded-xl border border-[#D9C8B3] bg-white px-4 text-base text-[#141013] outline-none focus:border-[#7A5418]";
const btn = "flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#141013] text-xs font-semibold uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors hover:bg-[#3A2A20] disabled:opacity-60";

export function AuthCard({ title, children }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F6F0E7] px-5 pb-16 pt-32">
      <div className="w-full max-w-md rounded-3xl border border-[#E3D5C3] bg-[#FBF8F3] p-8 md:p-10">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#7A5418]">Espace ambassadeur</p>
        <h1 className="font-display text-3xl text-[#141013]">{title}</h1>
        {children}
      </div>
    </main>
  );
}

export function AmbLogin({ onLogged }) {
  const [f, setF] = useState({ email: "", password: "" });
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      if (mode === "forgot") {
        await ambApi.post("/ambassador/forgot-password", { email: f.email });
        setMsg({ type: "ok", text: "Si cette adresse correspond à un ambassadeur, un lien vient de t'être envoyé par email." });
      } else {
        const { data } = await ambApi.post("/auth/login", f);
        if (data?.user?.role !== "ambassador") throw new Error("Cet accès est réservé aux ambassadeurs Ozentya.");
        ambToken.set(data.token);
        onLogged();
      }
    } catch (err) {
      setMsg({ type: "err", text: err?.response ? errMsg(err) : err.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard title={mode === "login" ? "Connexion" : "Mot de passe oublié"}>
      <form onSubmit={submit} data-testid="amb-login-form" className="mt-8 space-y-4">
        <input type="email" required placeholder="Adresse e-mail" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="amb-login-email" className={input} />
        {mode === "login" ? <input type="password" required placeholder="Mot de passe" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="amb-login-password" className={input} /> : null}
        {msg.text ? <p data-testid={msg.type === "ok" ? "amb-login-info" : "amb-login-error"} className={`text-sm ${msg.type === "ok" ? "text-[#2F5D3A]" : "text-[#A12C2C]"}`}>{msg.text}</p> : null}
        <button disabled={busy} data-testid="amb-login-submit" className={btn}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{mode === "login" ? "Me connecter" : "Recevoir le lien"}</button>
      </form>
      <button type="button" onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setMsg({ type: "", text: "" }); }} data-testid="amb-login-toggle" className="mt-5 text-sm text-[#5E544D] underline underline-offset-4">
        {mode === "login" ? "Mot de passe oublié ?" : "Retour à la connexion"}
      </button>
      <p className="mt-6 text-sm text-[#5E544D]">Pas encore ambassadeur ? <Link to="/ambassadeurs#candidature" className="text-[#7A5418] underline underline-offset-4">Candidate ici</Link></p>
    </AuthCard>
  );
}

export default function AmbSetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return setErr("8 caractères minimum.");
    if (f.password !== f.confirm) return setErr("Les deux mots de passe ne correspondent pas.");
    setBusy(true);
    setErr("");
    try {
      await ambApi.post("/ambassador/set-password", { token: params.get("token") || "", password: f.password });
      setDone(true);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthCard title="Choisis ton mot de passe">
      {done ? (
        <div data-testid="amb-setpwd-success" className="mt-8">
          <p className="text-base text-[#2F5D3A]">Ton mot de passe est enregistré.</p>
          <button onClick={() => nav("/espace-ambassadeur")} data-testid="amb-setpwd-login" className={`${btn} mt-6`}>Me connecter</button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="amb-setpwd-form" className="mt-8 space-y-4">
          <input type="password" placeholder="Nouveau mot de passe (8 caractères min.)" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="amb-setpwd-password" className={input} />
          <input type="password" placeholder="Confirme le mot de passe" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="amb-setpwd-confirm" className={input} />
          {err ? <p data-testid="amb-setpwd-error" className="text-sm text-[#A12C2C]">{err}</p> : null}
          <button disabled={busy} data-testid="amb-setpwd-submit" className={btn}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Enregistrer</button>
        </form>
      )}
    </AuthCard>
  );
}
